import { memo, useCallback, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { usePostHog } from 'posthog-js/react';
import { COLORS } from '../../styles/colors';
import { apiService } from '../../services/apiService';
import logger from '../../utils/logger';

/**
 * FinanceLeadForm - FCA-compliant finance enquiry form for a listing.
 *
 * Auto-populated with listing reference data (make, model, year, price).
 * Collects optional deposit amount, preferred monthly budget, and free-text notes.
 * CTA: "Get Finance Quote" — no APR or monthly payment shown (FCA compliance).
 *
 * On submit, calls `submitFinanceLead(feedEntryId, formData)` and shows
 * a success confirmation state.
 *
 * @param {Object}  props
 * @param {Object}  props.listing          - The listing object (must contain listing_snapshot and feed entry id)
 * @param {Function} [props.onSuccess]     - Callback after successful submission
 * @param {Function} [props.onClose]       - Callback to close/dismiss the form
 */
const FinanceLeadForm = ({ listing, onSuccess, onClose }) => {
  const posthog = usePostHog();
  const [depositAmount, setDepositAmount] = useState('');
  const [monthlyBudget, setMonthlyBudget] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const snapshot = listing?.listing_snapshot || {};
  const { make, model, year, price } = snapshot;
  const feedEntryId = listing?.id || listing?.feed_entry_id;

  const formatPrice = (val) => {
    if (val == null || isNaN(val)) return '--';
    return `\u00A3${Number(val).toLocaleString('en-GB')}`;
  };

  const handleSubmit = useCallback(async () => {
    if (!feedEntryId) {
      setError('Listing reference not available. Please try again.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const formData = {};
      if (depositAmount.trim()) {
        formData.deposit_amount = Number(depositAmount.replace(/[^0-9.]/g, ''));
      }
      if (monthlyBudget.trim()) {
        formData.preferred_monthly_budget = Number(monthlyBudget.replace(/[^0-9.]/g, ''));
      }
      if (notes.trim()) {
        formData.free_text_notes = notes.trim();
      }

      await apiService.submitFinanceLead(feedEntryId, formData);
      setSubmitted(true);

      posthog?.capture('finance_form_submitted', {
        listing_id: listing?.id,
        model_id: listing?.model_id,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('[FinanceLeadForm] Submit failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [feedEntryId, depositAmount, monthlyBudget, notes, onSuccess, listing?.id, listing?.model_id, posthog]);

  // ---- Success state ----
  if (submitted) {
    return (
      <View style={styles.container}>
        <View style={styles.successContainer}>
          <View style={styles.successIcon}>
            <Icon name="check-circle" size={48} color="#16A34A" />
          </View>
          <Text style={styles.successTitle}>Quote Request Sent</Text>
          <Text style={styles.successBody}>
            Your finance enquiry for the {year} {make} {model} has been submitted. A finance
            specialist will be in touch shortly.
          </Text>
          {onClose && (
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>Done</Text>
            </Pressable>
          )}
        </View>
      </View>
    );
  }

  // ---- Form state ----
  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Get Finance Quote</Text>
        {onClose && (
          <Pressable
            onPress={onClose}
            style={styles.headerClose}
            accessibilityRole="button"
            accessibilityLabel="Close finance form"
          >
            <Icon name="close" size={20} color={COLORS.textTertiary} />
          </Pressable>
        )}
      </View>

      {/* Listing reference (read-only) */}
      <View style={styles.listingRef}>
        <Icon name="car" size={20} color={COLORS.primary} />
        <View style={styles.listingRefText}>
          <Text style={styles.listingRefTitle}>
            {year} {make} {model}
          </Text>
          <Text style={styles.listingRefPrice}>{formatPrice(price)}</Text>
        </View>
      </View>

      {/* Form fields */}
      <View style={styles.formFields}>
        <View style={styles.fieldGroup}>
          <Text style={styles.fieldLabel}>Deposit Amount (optional)</Text>
          <View style={styles.inputWrapper}>
            <Text style={styles.currencyPrefix}>{'\u00A3'}</Text>
            <TextInput
              style={styles.inputNoBorder}
              value={depositAmount}
              onChangeText={setDepositAmount}
              placeholder="e.g. 2,000"
              placeholderTextColor={COLORS.textTertiary}
              keyboardType="numeric"
              editable={!submitting}
            />
          </View>
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.fieldLabel}>Preferred Monthly Budget (optional)</Text>
          <View style={styles.inputWrapper}>
            <Text style={styles.currencyPrefix}>{'\u00A3'}</Text>
            <TextInput
              style={styles.inputNoBorder}
              value={monthlyBudget}
              onChangeText={setMonthlyBudget}
              placeholder="e.g. 300"
              placeholderTextColor={COLORS.textTertiary}
              keyboardType="numeric"
              editable={!submitting}
            />
          </View>
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.fieldLabel}>Additional Notes (optional)</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={notes}
            onChangeText={setNotes}
            placeholder="Any special requirements or questions..."
            placeholderTextColor={COLORS.textTertiary}
            multiline
            numberOfLines={3}
            textAlignVertical="top"
            editable={!submitting}
          />
        </View>
      </View>

      {/* Error */}
      {error && (
        <View style={styles.errorContainer}>
          <Icon name="alert-circle-outline" size={16} color="#EF4444" />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {/* FCA compliance note */}
      <Text style={styles.disclaimer}>
        This is an enquiry only. No credit check will be performed at this stage. Finance is subject
        to status and availability.
      </Text>

      {/* Submit CTA */}
      <Pressable
        onPress={handleSubmit}
        style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
        disabled={submitting}
        accessibilityRole="button"
        accessibilityLabel="Get Finance Quote"
      >
        {submitting ? (
          <ActivityIndicator size="small" color={COLORS.white} />
        ) : (
          <Text style={styles.submitButtonText}>Get Finance Quote</Text>
        )}
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.white,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: COLORS.borderColor,
    overflow: 'hidden',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderColor,
  },
  headerTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 18,
    color: COLORS.textPrimary,
  },
  headerClose: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.lightGray,
  },

  // Listing reference
  listingRef: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    marginHorizontal: 20,
    marginTop: 16,
    backgroundColor: COLORS.lightGray,
    borderRadius: 10,
  },
  listingRefText: {
    flex: 1,
  },
  listingRefTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: COLORS.textPrimary,
    lineHeight: 20,
  },
  listingRefPrice: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: COLORS.textSecondary,
  },

  // Form fields
  formFields: {
    padding: 20,
    gap: 16,
  },
  fieldGroup: {
    gap: 6,
  },
  fieldLabel: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: COLORS.textSecondary,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.borderColor,
    borderRadius: 10,
    backgroundColor: COLORS.white,
  },
  currencyPrefix: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: COLORS.textTertiary,
    paddingLeft: 14,
  },
  input: {
    flex: 1,
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: COLORS.textPrimary,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: COLORS.borderColor,
    borderRadius: 10,
    outlineStyle: 'none',
  },
  inputNoBorder: {
    flex: 1,
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: COLORS.textPrimary,
    paddingVertical: 12,
    paddingHorizontal: 12,
    outlineStyle: 'none',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },

  // Error
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 20,
    marginBottom: 8,
  },
  errorText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: '#EF4444',
  },

  // Disclaimer
  disclaimer: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 11,
    color: COLORS.textTertiary,
    lineHeight: 16,
    paddingHorizontal: 20,
    marginBottom: 16,
  },

  // Submit button
  submitButton: {
    backgroundColor: COLORS.primary,
    marginHorizontal: 20,
    marginBottom: 20,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    color: COLORS.white,
  },

  // Success state
  successContainer: {
    padding: 32,
    alignItems: 'center',
    gap: 12,
  },
  successIcon: {
    marginBottom: 4,
  },
  successTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 20,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  successBody: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: COLORS.textSecondary,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 320,
  },
  closeButton: {
    marginTop: 8,
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
  },
  closeButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: COLORS.white,
  },
});

export default memo(FinanceLeadForm);
